<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Exames para Análise</h4>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button class="btn btn-primary" @click="loadExams">
                    <i class="bx bx-refresh me-1"></i> Atualizar
                  </button>
                </div>
              </div>
            </div>

            <div v-if="loading" class="text-center">
              <div class="spinner-border" role="status">
              </div>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th scope="col">ID do Exame</th>
                    <th scope="col">Paciente</th>
                    <th scope="col">Tipo de Exame</th>
                    <th scope="col">Data</th>
                    <th scope="col">Status</th>
                    <th scope="col">Prioridade</th>
                    <th scope="col">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="exam in exams" :key="exam.id">
                    <td>{{ exam.id }}</td>
                    <td>{{ exam.patient_name || 'N/A' }}</td>
                    <td>{{ exam.exam_type || 'N/A' }}</td>
                    <td>{{ formatDate(exam.created_at) }}</td>
                    <td>
                      <span 
                        :class="getStatusBadgeClass(exam.status)"
                      >
                        {{ getStatusLabel(exam.status) }}
                      </span>
                    </td>
                    <td>
                      <span 
                        :class="getPriorityBadgeClass(exam.priority)"
                      >
                        {{ getPriorityLabel(exam.priority) }}
                      </span>
                    </td>
                    <td>
                      <div class="d-flex gap-2">
                        <button 
                          class="btn btn-outline-primary btn-sm"
                          @click="viewExam(exam)"
                          title="Visualizar Exame"
                        >
                          <i class="bx bx-show"></i>
                        </button>
                        <button 
                          class="btn btn-outline-success btn-sm"
                          @click="createReport(exam)"
                          title="Criar Laudo"
                        >
                          <i class="bx bx-file-plus"></i>
                        </button>
                        <button 
                          v-if="exam.dicom_available"
                          class="btn btn-outline-info btn-sm"
                          @click="viewDicom(exam)"
                          title="Visualizar DICOM"
                        >
                          <i class="bx bx-image"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="exams.length === 0">
                    <td colspan="7" class="text-center">Nenhum exame encontrado para análise</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Paginação -->
            <div v-if="exams.length > 0" class="row mt-3">
              <div class="col-sm-6">
                <div>
                  Mostrando {{ exams.length }} exames
                </div>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button 
                    class="btn btn-outline-secondary btn-sm me-2"
                    @click="loadMore"
                    :disabled="loading"
                  >
                    Carregar Mais
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { doctorService } from "@/api/doctor";

export default {
  page: {
    title: "Exames para Análise",
    meta: [{ name: "description", content: "Exames disponíveis para análise médica" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Exames para Análise",
      items: [
        {
          text: "Médico",
          href: "/doctor",
        },
        {
          text: "Exames",
          active: true,
        },
      ],
      exams: [],
      loading: false,
      skip: 0,
      limit: 50,
    };
  },
  async created() {
    await this.loadExams();
  },
  methods: {
    async loadExams() {
      this.loading = true;
      try {
        const response = await doctorService.getExamsForReview({
          skip: this.skip,
          limit: this.limit,
        });
        this.exams = response.data;
      } catch (error) {
        console.error("Erro ao carregar exames:", error);
        this.$toast.error("Erro ao carregar exames");
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      this.skip += this.limit;
      this.loading = true;
      try {
        const response = await doctorService.getExamsForReview({
          skip: this.skip,
          limit: this.limit,
        });
        this.exams.push(...response.data);
      } catch (error) {
        console.error("Erro ao carregar mais exames:", error);
        this.$toast.error("Erro ao carregar mais exames");
      } finally {
        this.loading = false;
      }
    },

    viewExam(exam) {
      this.$router.push(`/doctor/exams/${exam.id}`);
    },

    createReport(exam) {
      this.$router.push(`/doctor/reports/create?exam_id=${exam.id}`);
    },

    viewDicom(exam) {
      this.$router.push(`/dicom/viewer?study_id=${exam.dicom_study_id}`);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('pt-BR');
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'Pendente',
        in_progress: 'Em Análise',
        completed: 'Concluído',
        cancelled: 'Cancelado',
      };
      return labels[status] || status;
    },

    getStatusBadgeClass(status) {
      const classes = {
        pending: 'badge bg-soft-warning text-warning',
        in_progress: 'badge bg-soft-info text-info',
        completed: 'badge bg-soft-success text-success',
        cancelled: 'badge bg-soft-danger text-danger',
      };
      return classes[status] || 'badge bg-soft-secondary text-secondary';
    },

    getPriorityLabel(priority) {
      const labels = {
        low: 'Baixa',
        normal: 'Normal',
        high: 'Alta',
        urgent: 'Urgente',
      };
      return labels[priority] || priority;
    },

    getPriorityBadgeClass(priority) {
      const classes = {
        low: 'badge bg-soft-secondary text-secondary',
        normal: 'badge bg-soft-primary text-primary',
        high: 'badge bg-soft-warning text-warning',
        urgent: 'badge bg-soft-danger text-danger',
      };
      return classes[priority] || 'badge bg-soft-secondary text-secondary';
    },
  },
};
</script>
